import { useNavigate, Navigate, Link } from 'react-router-dom';
import { useAuth } from '../../hooks/useAuth';
import { CodeConfirmationForm } from '@/ui/components/CodeConfirmationForm/CodeConfirmationForm';
import { key } from '@/assets/images/emojis';
import styles from './PhoneConfirmation.module.scss';
import { useEffect, useState } from 'react';
import { routes } from '@/routes/routes';
import { useError } from '@/providers/Error';
import { genericMessages, phoneOtpMessages } from '@/constants/errorMessages';
import { useAppRedirectAfterSignup } from '../../hooks/useAppRedirectAfterSignup';

const OTP_LENGTH = 6;
const RESEND_DELAY = 120;

function PhoneConfirmationPage() {
  const navigate = useNavigate();
  const { error, setError } = useError();
  const [code, setCode] = useState<string>('');

  const {
    requestOtp,
    isRequestingOtp,
    isSigningUp,
    signUp,
    isAuthenticated,
    authPhoneNumber,
    requestOtpError,
    signUpError,
  } = useAuth();

  const { isResolving: isResolvingFlow, shouldRedirectToApp } =
    useAppRedirectAfterSignup();

  // Keep the form busy while the flow is being resolved so the screen does not
  // look idle between a successful sign-in and the redirect.
  const isLoading =
    isRequestingOtp || isSigningUp || (isAuthenticated && isResolvingFlow);

  useEffect(() => {
    if (!isAuthenticated || isResolvingFlow) {
      return;
    }

    navigate(shouldRedirectToApp ? routes.appRedirect : routes.wizard.base);
  }, [isAuthenticated, isResolvingFlow, shouldRedirectToApp]);

  useEffect(() => {
    if (requestOtpError || signUpError) {
      setError(genericMessages.unexpected);
    }
  }, [requestOtpError, signUpError]);

  if (!authPhoneNumber) {
    return <Navigate to={routes.public.signup} replace />;
  }

  const handleRequestOtp = async () => {
    const response = await requestOtp({ phone: authPhoneNumber });
    if (!response.isSuccess) {
      setError(
        phoneOtpMessages[response.error as keyof typeof phoneOtpMessages]
      );
      return;
    }

    setCode('');
  };

  const handleCodeChange = async (value: string) => {
    setCode(value);

    if (value.length === OTP_LENGTH) {
      const response = await signUp({ phone: authPhoneNumber, code: value });
      if (!response.isSuccess) {
        setError(
          phoneOtpMessages[response.error as keyof typeof phoneOtpMessages]
        );
        return;
      }
    }
  };

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <img src={key} alt="Key emoji" className={styles.emoji} />
        <h1 className={styles.title}>Introdu codul de confirmare</h1>
        <p className={styles.subtitle}>
          Confirmă codul trimis prin SMS către <br />
          {authPhoneNumber} (
          <Link to={routes.public.signup} className={styles.changeNumberLink}>
            Schimbă numărul
          </Link>
          )
        </p>
      </div>

      <CodeConfirmationForm
        value={code}
        className={styles.codeConfirmationForm}
        onChange={handleCodeChange}
        onResendCode={handleRequestOtp}
        isLoading={isLoading}
        length={OTP_LENGTH}
        resendDelay={RESEND_DELAY}
        error={error}
        autoFocus
      />
    </div>
  );
}

export default PhoneConfirmationPage;
